import type { IntakeRecord } from '../store/slices/intakeSlice';
import type { AgencyClinicResponse } from '../services/api/types.gen';
import {
  formatDateToZeroPaddedMMDDYYYY,
  formatDateTimeInAgencyZone,
} from './dateUtils';
import type { IntakeFormSchema } from '../config/intakeFormSchema';
import { getDropdownOptionLabel } from '../config/intakeFormSchema';
import { NON_ADMIT_REASON_LABELS } from '../constants/nonAdmitReasons';

type RowIntake = IntakeRecord & {
  tab_statuses?: Record<string, string | undefined> | null;
  agency_clinic?: AgencyClinicResponse | null;
};

function fieldsGet(intake: RowIntake, key: string): unknown {
  const fromFields = intake.fields?.[key];
  if (fromFields !== undefined && fromFields !== null && fromFields !== '') {
    return fromFields;
  }
  const direct = (intake as unknown as Record<string, unknown>)[key];
  return direct ?? '';
}

/**
 * Column-first read: prefer the persisted intake column, fall back to the
 * extracted fields blob. Note fieldsGet has the opposite precedence — these
 * dates are canonical once the scheduling forms have written the column.
 */
function columnFirstDateGet(
  intake: RowIntake,
  key: 'roc_date' | 'recert_date'
): unknown {
  const direct = intake[key];
  if (direct !== undefined && direct !== null && direct !== '') {
    return direct;
  }
  const fromFields = intake.fields?.[key];
  if (fromFields !== undefined && fromFields !== null && fromFields !== '') {
    return fromFields;
  }
  return '';
}

function hasNonEmptyValue(value: unknown): boolean {
  return value != null && value !== '' && String(value).trim() !== '';
}

function firstNonEmptyValue(...values: unknown[]): unknown {
  return values.find(hasNonEmptyValue) ?? '';
}

function isTruthyFlag(value: unknown): boolean {
  return (
    value === true ||
    (typeof value === 'string' && value.trim().toLowerCase() === 'true')
  );
}

function socDateGet(intake: RowIntake): unknown {
  const rogerSoc = intake.soc_date;
  const kinnserSoc = intake.kinnser_soc_date;
  const isKinnserTarget =
    isTruthyFlag(fieldsGet(intake, 'push_to_kinnser')) ||
    isTruthyFlag(fieldsGet(intake, 'kinnser_create_episode'));
  const isRogerTarget =
    isTruthyFlag(fieldsGet(intake, 'roger_create_episode')) ||
    isTruthyFlag(fieldsGet(intake, 'push_to_roger'));

  if (isKinnserTarget) {
    return firstNonEmptyValue(kinnserSoc, rogerSoc);
  }
  if (isRogerTarget) {
    return firstNonEmptyValue(rogerSoc, kinnserSoc);
  }
  return firstNonEmptyValue(rogerSoc, kinnserSoc);
}

function listJoin(val: unknown): string {
  if (Array.isArray(val)) return val.map(String).join(', ');
  return val != null && val !== '' ? String(val) : '';
}

function boolLabel(val: boolean | undefined): string {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  return '';
}

const TAB_STATUS_LABELS: Record<string, string> = {
  complete: 'Complete',
  complete_with_errors: 'Complete (errors)',
  skipped: 'Skipped',
  incomplete: 'Incomplete',
};

function tabStatusLabel(intake: RowIntake, tabKey: string): string {
  const ts = intake.tab_statuses ?? {};
  const val = ts[tabKey];
  if (val == null || val === '') return '';
  return TAB_STATUS_LABELS[val] ?? String(val);
}

const OVERRIDE_KEY_MAP: Record<string, string> = {
  home_health_orders_attached: 'order_attached',
  home_health_orders_signed: 'order_signed',
  face_to_face_visits_attached: 'f2f_attached',
  face_to_face_visits_signed: 'f2f_signed',
};

function codingExtractionStatus(
  intake: RowIntake,
  key: 'home_health_orders' | 'face_to_face_visits',
  check: 'attached' | 'signed'
): string {
  const fields = intake.fields ?? {};
  const overrides =
    (fields.document_validation_manual_overrides as Record<
      string,
      { value?: boolean } | unknown
    > | null) ?? {};
  const overrideKey = OVERRIDE_KEY_MAP[`${key}_${check}`] ?? '';
  if (overrideKey && overrides[overrideKey] != null) {
    const o = overrides[overrideKey] as { value?: boolean };
    if (
      o &&
      typeof o === 'object' &&
      o.value !== undefined &&
      o.value !== null
    ) {
      return o.value ? 'Yes' : 'No';
    }
  }

  const ce = intake.coding_extraction ?? {};
  const raw = ce[key];
  if (!Array.isArray(raw)) return '';

  if (check === 'attached') {
    return raw.length > 0 ? 'Yes' : 'No';
  }

  for (const item of raw) {
    if (item && typeof item === 'object') {
      const validations = (item as { validations?: Record<string, unknown> })
        .validations;
      const isSigned = validations?.is_signed as
        { value?: boolean } | undefined;
      if (isSigned?.value === true) return 'Yes';
    }
  }
  return raw.length > 0 ? 'No' : '';
}

function formatDateish(v: unknown): string {
  if (v == null || v === '') return '';
  if (v instanceof Date) {
    return formatDateToZeroPaddedMMDDYYYY(v) || '';
  }
  if (typeof v === 'string') {
    // Normalize ISO-ish date/datetime strings to MM/DD/YYYY for display.
    // - "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss..." -> MM/DD/YYYY
    // - already-formatted strings (e.g. "N/A") pass through
    const mmddyyyy = formatDateToZeroPaddedMMDDYYYY(v);
    return mmddyyyy || v;
  }
  return String(v);
}

/** Epoch millis for a timestamp column value; null when absent/unparseable. */
function toEpochMs(value: Date | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Compact humanized time-to-push: "2d 4h", "3h 12m", or "45m". Empty when the
 * intake was never pushed (no pushed_at) or the difference is negative.
 */
function formatTimeToPush(
  createdAt: Date | string | null | undefined,
  pushedAt: string | null | undefined
): string {
  const createdMs = toEpochMs(createdAt);
  const pushedMs = toEpochMs(pushedAt);
  if (createdMs == null || pushedMs == null) return '';

  const diffMs = pushedMs - createdMs;
  if (diffMs < 0) return '';

  const totalMinutes = Math.floor(diffMs / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** SOC/ROC/REC parts; aligned with backend `intake_export._soc_roc_parts`. */
export function getIntakeSocRocParts(intake: IntakeRecord): {
  visitType: string;
  visitDate: string;
} {
  const i = intake as RowIntake;
  const visitNorm = String(fieldsGet(i, 'kinnser_visit_type') ?? '')
    .trim()
    .toLowerCase();
  const isRoc = visitNorm === 'roc';

  if (visitNorm === 'rec') {
    const recertRaw = columnFirstDateGet(i, 'recert_date');
    return {
      visitType: 'REC',
      visitDate: hasNonEmptyValue(recertRaw) ? formatDateish(recertRaw) : '',
    };
  }

  const rocRaw = columnFirstDateGet(i, 'roc_date');
  const hasRocRaw = hasNonEmptyValue(rocRaw);
  const rocFmt = hasRocRaw ? formatDateish(rocRaw) : '';

  const socRaw = socDateGet(i);
  const hasSocRaw = hasNonEmptyValue(socRaw);
  const socFmt = hasSocRaw ? formatDateish(socRaw) : '';

  if (isRoc) {
    return { visitType: 'ROC', visitDate: rocFmt };
  }
  if (hasSocRaw && socFmt) {
    return { visitType: 'SOC', visitDate: socFmt };
  }
  if (hasRocRaw && rocFmt) {
    return { visitType: 'ROC', visitDate: rocFmt };
  }
  return { visitType: '', visitDate: '' };
}

/** SOC/ROC cell: aligned with backend legacy combined cell (Visit Type + Visit Date). */
export function formatIntakeSocRocCell(intake: IntakeRecord): string {
  const { visitType, visitDate } = getIntakeSocRocParts(intake);
  if (!visitType) return '';
  if (visitDate) return `${visitType} ${visitDate}`;
  return visitType;
}

/**
 * Best-effort display filename for an intake's uploaded document, used to
 * identify a still-processing intake in the list. Prefers the first attached
 * document's display name, then the basename of the uploaded PDF path, and
 * returns null when neither is available.
 */
export function getIntakeUploadFilename(intake: IntakeRecord): string | null {
  const docName = intake.documents?.[0]?.file_name?.trim();
  if (docName) return docName;

  const path = intake.fax_pdf_path?.trim();
  if (path) {
    const base = path.split('/').pop()?.split('?')[0]?.trim();
    if (base) return base;
  }

  return null;
}

/**
 * Plain-text cell value for a catalog column id (not used for composite patient/status/insurance/assignee).
 */
export function getIntakeManagerColumnDisplayString(
  intake: IntakeRecord,
  columnId: string,
  schema?: IntakeFormSchema
): string {
  const i = intake as RowIntake;

  switch (columnId) {
    case 'patient_first_name':
      return i.patient_first_name ?? '';
    case 'patient_last_name':
      return i.patient_last_name ?? '';
    case 'patient_middle_name':
      return i.patient_middle_name ?? '';
    case 'patient_suffix':
      return i.patient_suffix ?? '';
    case 'patient_dob':
      return formatDateish(i.patient_dob);
    case 'patient_gender':
      return i.patient_gender != null ? String(i.patient_gender) : '';
    case 'patient_ssn':
      return i.patient_ssn ?? '';
    case 'patient_phone':
      return i.patient_phone ?? '';
    case 'patient_secondary_phone':
      return i.patient_secondary_phone ?? '';
    case 'patient_email':
      return i.patient_email ?? '';
    case 'street_address':
      return i.patient_street_address ?? '';
    case 'city':
      return i.patient_city ?? '';
    case 'state':
      return i.patient_state ?? '';
    case 'zip_code':
      return i.patient_zip_code ?? '';
    case 'county':
      return String(fieldsGet(i, 'patient_county') ?? '');
    case 'primary_language':
      return String(fieldsGet(i, 'patient_primary_language') ?? '');
    case 'race':
      return String(fieldsGet(i, 'patient_race') ?? '');
    case 'ethnicity':
      return String(fieldsGet(i, 'patient_ethnicity') ?? '');
    case 'height':
      return String(fieldsGet(i, 'patient_height') ?? '');
    case 'weight':
      return String(fieldsGet(i, 'patient_weight') ?? '');
    case 'medicaid_id':
      return String(fieldsGet(i, 'medicaid_id_number') ?? '');
    case 'emergency_contact_name':
      return String(fieldsGet(i, 'emergency_contact_name') ?? '');
    case 'emergency_contact_relationship':
      return String(fieldsGet(i, 'emergency_contact_relationship') ?? '');
    case 'emergency_contact_phone':
      return String(fieldsGet(i, 'emergency_contact_phone') ?? '');
    case 'insurance_mapped':
      return i.agency_insurance?.name ?? '';
    case 'primary_insurance':
      return String(
        fieldsGet(i, 'primary_insurance') ?? i.primary_insurance ?? ''
      );
    case 'primary_member_id':
      return String(
        fieldsGet(i, 'primary_member_id') ?? i.primary_member_id ?? ''
      );
    case 'secondary_insurance':
      return String(
        fieldsGet(i, 'secondary_insurance') ?? i.secondary_insurance ?? ''
      );
    case 'secondary_member_id':
      return String(
        fieldsGet(i, 'secondary_member_id') ?? i.secondary_member_id ?? ''
      );
    case 'referring_provider':
      return i.referring_physician ?? '';
    case 'referring_phone':
      return String(fieldsGet(i, 'referring_phone') ?? i.referring_phone ?? '');
    case 'referring_fax':
      return String(fieldsGet(i, 'referring_fax') ?? i.referring_fax ?? '');
    case 'referral_source':
      return i.referral_source ?? '';
    case 'external_referral_source':
      return i.kinnser_external_referral_name ?? '';
    case 'internal_referral_source':
      return i.internal_referral_source_name ?? '';
    case 'discharge_facility':
      return String(
        fieldsGet(i, 'discharge_facility_name') ??
          i.discharge_facility_name ??
          ''
      );
    case 'discharge_facility_type': {
      const code = String(
        fieldsGet(i, 'discharge_facility_type') ??
          i.discharge_facility_type ??
          ''
      );
      return schema
        ? getDropdownOptionLabel(schema, 'discharge_facility_type', code)
        : code;
    }
    case 'f2f_physician':
      return String(fieldsGet(i, 'f2f_physician') ?? '');
    case 'primary_diagnosis':
      return String(
        fieldsGet(i, 'primary_diagnosis') ?? i.primary_diagnosis ?? ''
      );
    case 'diagnosis_codes':
      return listJoin(fieldsGet(i, 'diagnosis_codes') ?? i.diagnosis_codes);
    case 'services_requested':
      return listJoin(
        fieldsGet(i, 'services_requested') ?? i.services_requested
      );
    case 'allergies':
      return String(fieldsGet(i, 'allergies') ?? '');
    case 'past_medical_history':
      return String(fieldsGet(i, 'past_medical_history') ?? '');
    case 'homebound_reason':
      return String(fieldsGet(i, 'homebound_reason') ?? '');
    case 'physician_order_date':
      return formatDateish(i.physician_order_date);
    case 'order_attached':
      return codingExtractionStatus(i, 'home_health_orders', 'attached');
    case 'order_signed':
      return codingExtractionStatus(i, 'home_health_orders', 'signed');
    case 'f2f_encounter_date':
      return formatDateish(i.f2f_encounter_date);
    case 'f2f_attached':
      return codingExtractionStatus(i, 'face_to_face_visits', 'attached');
    case 'f2f_signed':
      return codingExtractionStatus(i, 'face_to_face_visits', 'signed');
    case 'extraction':
      return tabStatusLabel(i, 'extraction');
    case 'eligibility':
      return tabStatusLabel(i, 'eligibility');
    case 'prior_auth':
      return tabStatusLabel(i, 'prior_auth');
    case 'ready_to_call':
      return tabStatusLabel(i, 'ready_to_call');
    case 'ready_to_call_comments':
      return i.comments ?? '';
    case 'scheduling':
      return tabStatusLabel(i, 'scheduling');
    case 'push_to_emr':
      return tabStatusLabel(i, 'push_to_emr');
    case 'clinic':
      return i.agency_clinic?.name ?? '';
    case 'archived':
      return boolLabel(i.archived);
    case 'non_admit':
      return boolLabel(i.non_admit);
    case 'non_admit_reason':
      return i.non_admit_reason
        ? (NON_ADMIT_REASON_LABELS[i.non_admit_reason] ?? '')
        : '';
    case 'non_admit_reason_details':
      return i.non_admit_reason_details ?? '';
    case 'visit_type':
      return getIntakeSocRocParts(intake).visitType;
    case 'visit_date':
      return getIntakeSocRocParts(intake).visitDate;
    case 'soc_date':
      return formatIntakeSocRocCell(intake);
    case 'discharge_date':
      return formatDateish(i.discharge_date);
    case 'created':
      return formatDateish(i.created_at);
    case 'created_timestamp':
      return formatDateTimeInAgencyZone(
        typeof i.created_at === 'string'
          ? i.created_at
          : i.created_at.toISOString()
      );
    case 'pushed_timestamp':
      return formatDateTimeInAgencyZone(i.pushed_at);
    case 'time_to_push':
      return formatTimeToPush(i.created_at, i.pushed_at);
    default:
      return '';
  }
}
